/**
 * Finnish date and number helpers. The client owns the calendar date: every
 * key is the device's local day, formatted YYYY-MM-DD.
 */

export const FI_DAYS = ['sunnuntai', 'maanantai', 'tiistai', 'keskiviikko', 'torstai', 'perjantai', 'lauantai'];
export const FI_MONTHS = ['tammikuu', 'helmikuu', 'maaliskuu', 'huhtikuu', 'toukokuu', 'kesäkuu', 'heinäkuu', 'elokuu', 'syyskuu', 'lokakuu', 'marraskuu', 'joulukuu'];
export const FI_WEEKDAYS_SHORT = ['ma', 'ti', 'ke', 'to', 'pe', 'la', 'su'];

const DATE_KEY = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH_KEY = /^(\d{4})-(\d{2})$/;

const pad = (n: number): string => String(n).padStart(2, '0');

/** Local calendar date of a Date as YYYY-MM-DD. */
export function toKey(d: Date): string {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Today's key from the device's local clock. */
export function todayKey(): string {
    return toKey(new Date());
}

/** Parse YYYY-MM-DD into a local-midnight Date. Throws on a malformed key. */
export function fromKey(key: string): Date {
    const m = DATE_KEY.exec(key);
    if (!m) throw new Error(`Invalid date key: ${key}`);
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** True when the string is a real YYYY-MM-DD calendar date. */
export function isDateKey(key: string): boolean {
    if (!DATE_KEY.test(key)) return false;
    const d = fromKey(key);
    return toKey(d) === key;
}

export function isMonthKey(key: string): boolean {
    const m = MONTH_KEY.exec(key);
    if (!m) return false;
    const month = Number(m[2]);
    return month >= 1 && month <= 12;
}

/** YYYY-MM of a Date. */
export function toMonthKey(d: Date): string {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}

export function addDays(d: Date, n: number): Date {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
}

export function addMonths(d: Date, n: number): Date {
    return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

/** Whole days from a to b (b - a), both taken as local calendar dates. */
export function diffDays(a: Date | string, b: Date | string): number {
    const da = typeof a === 'string' ? fromKey(a) : a;
    const db = typeof b === 'string' ? fromKey(b) : b;
    const ua = Date.UTC(da.getFullYear(), da.getMonth(), da.getDate());
    const ub = Date.UTC(db.getFullYear(), db.getMonth(), db.getDate());
    return Math.round((ub - ua) / 86_400_000);
}

/** "perjantai 11.9." */
export function fmtDay(d: Date | string): string {
    const x = typeof d === 'string' ? fromKey(d) : d;
    return `${FI_DAYS[x.getDay()]} ${x.getDate()}.${x.getMonth() + 1}.`;
}

/** "11.9." */
export function fmtShort(d: Date | string): string {
    const x = typeof d === 'string' ? fromKey(d) : d;
    return `${x.getDate()}.${x.getMonth() + 1}.`;
}

/** "11.9.2026" */
export function fmtDate(d: Date | string): string {
    const x = typeof d === 'string' ? fromKey(d) : d;
    return `${x.getDate()}.${x.getMonth() + 1}.${x.getFullYear()}`;
}

/** Weekday name of a date: "perjantai". */
export function weekdayName(d: Date | string): string {
    const x = typeof d === 'string' ? fromKey(d) : d;
    return FI_DAYS[x.getDay()];
}

/** "syyskuu 2026" */
export function monthTitle(d: Date): string {
    return `${FI_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** "Syyskuu" */
export function monthName(d: Date, capitalized = false): string {
    const name = FI_MONTHS[d.getMonth()];
    return capitalized ? capitalize(name) : name;
}

export function capitalize(s: string): string {
    return s ? s[0].toUpperCase() + s.slice(1) : s;
}

/** ISO 8601 week number (weeks start on Monday, week 1 holds 4 January). */
export function isoWeek(d: Date | string): number {
    const x = typeof d === 'string' ? fromKey(d) : d;
    const t = new Date(Date.UTC(x.getFullYear(), x.getMonth(), x.getDate()));
    const day = t.getUTCDay() || 7;
    t.setUTCDate(t.getUTCDate() + 4 - day);
    const y0 = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
    return Math.ceil(((t.getTime() - y0.getTime()) / 86_400_000 + 1) / 7);
}

/** Monday of the week containing d (local). */
export function mondayOf(d: Date): Date {
    return addDays(d, -((d.getDay() + 6) % 7));
}

/** One decimal with a Finnish decimal comma: 69.8 -> "69,8". */
export function fmtKg(n: number): string {
    return (Math.round(n * 10) / 10).toFixed(1).replace('.', ',');
}

/** Signed one-decimal delta: -0.4 -> "-0,4", 0.4 -> "+0,4", 0 -> "0,0". */
export function fmtDelta(n: number): string {
    const r = Math.round(n * 10) / 10;
    const s = fmtKg(Math.abs(r));
    if (r < 0) return `-${s}`;
    if (r > 0) return `+${s}`;
    return s;
}

/** Parse a user-typed decimal ("69,8" or "69.8"). Returns null for empty or NaN. */
export function parseDecimal(raw: string): number | null {
    const s = raw.trim().replace(',', '.');
    if (!s) return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
}
